import React, { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { bumpReportTableParser } from 'parser/bumpReportTableParser';

const errorMessage: string = 'Something went wrong!';

interface Props {
    url: string;
    method: 'post' | 'get' | 'put' | 'delete';
    queryKey: any[];
    onComplete?: Function;
    onError?: Function;
    variables?: any;
    appConfig?: any;
    queryConfig?: {
        enabled?: boolean;
    };
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
    promoTypesObj: any;
    hasMoreItems: boolean;
    setPromoTypesObj: Function;
    setHasMoreItems: Function;
}

function useFetchPromoCalenderRQ({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    appConfig = {},
    queryConfig = {},
}: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [hasMoreItems, setHasMoreItems] = useState(true);
    const [promoTypesObj, setPromoTypesObj] = useState<any | null>({});

    const bumpReportConfig = getConfig('bumpReport', appConfig);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setError(null);
            setLoading(true);
            if (variables.page === 1) {
                setHasMoreItems(true);
                setData(null);
            }

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error: any) {
                onFetchError(error?.message);
                return { data: { error: error?.message } };
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }

    //error handling when fetching from cache
    if (apiData?.data && 'error' in apiData?.data && !error) {
        onFetchError(apiData?.data?.error);
    }

    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (payload && 'calendar_details' in payload) {
                if (payload['calendar_details'].length) {
                    const resultData = bumpReportTableParser(
                        {
                            data: payload.calendar_details,
                            startWeek: payload?.period_start || [],
                            endWeek: payload?.period_end || [],
                        },
                        variables.page,
                        bumpReportConfig,
                        appConfig,
                        promoTypesObj,
                        bumpReportConfig?.promoCalenderAttributeLevels?.[variables.investment_type],
                        variables?.attribute_levels,
                    );
                    const parsedTableData = resultData.result;
                    const distinctPromoTypes = resultData.promoTypesObj || {};
                    setPromoTypesObj(distinctPromoTypes);
                    //if coming from infinite scroll append the data to existing data
                    setData(data && variables.page > 1 ? [...data, ...parsedTableData] : parsedTableData);
                    return;
                }
                setHasMoreItems(false);
            }
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
        setHasMoreItems(false);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        hasMoreItems,
        promoTypesObj,
        setHasMoreItems,
        setPromoTypesObj,
    };
    return outputData;
}

export { useFetchPromoCalenderRQ };
